/// <reference types="@yandex-int/rum-counter/dist/inline/interface.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/inline/io.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/bundle/send.d.ts" />
/// <reference types="@yandex-int/rum-counter/dist/bundle/implementation.d.ts" />

import { isObject, JSONCompatible, Primitive, safeJsonStringify } from '@direct-frontend/stdlib';
import { createEvent } from '@direct-frontend/stdlib';

import { HERO_ELEMENT_ID, ElementTimingObserver } from './element-timing';
import { buildRumId } from './buildRumId';
import { buildRumUrl } from './buildRumUrl';
import { getCLSAdditionalParams } from './attribution/getCLSAdditionalParams';
import { getINPAdditionalParams } from './attribution/getINPAdditionalParams';
import { getLCPAdditionalParams } from './attribution/getLCPAdditionalParams';
import { getLoAFAdditionalParams } from './attribution/getLoAFAdditionalParams';
import {
    createRequestWaterfallObserver,
    RequestWaterfallObserver,
    RequestWaterfallObserverOptions,
} from './requestWaterfallObserver';
import { createScrollObserver, ScrollObserver } from './scrollObserver';
import { subscribeToPageHide, subscribeToPageShow } from './pageVisibility';
import { RumError } from './RumError';
import { debugLog } from './debugLog';
import { LongAnimationFrameCollector, LongestAnimationFrameObserver } from './longest-animation-frame';
import { startCapturingInteractionTargets } from './captureInteractionTargets';
import {
    cleanupElementTimingObserver,
    getElementTimingObserver,
    setElementTimingObserver,
} from './element-timing/globalElementTimingObserver';
import { TimeToInteractiveTracker } from './time-to-interactive';

// BlockStat
// Подробнее: https://api.stat.yandex-team.ru/_api/dictionary/_blockstat/
const BlockStat = {
    ActionStartTime: '689.2322',
    SubPageIndex: '2925',
    SubPageName: '2924',
} as const;

// Флаг инициализации Rum счетчика.
let isRumInited: boolean = false;

// Глобальный индекс для подстраниц
// Используется для разметки подстраницы при SPA переходе
let globalSubPageIndex = 0;

let unsubscribePageHide: null | (() => void) = null;
let unsubscribePageShow: null | (() => void) = null;

// Детектор долгих отрисовок (подвисаний интерфейса)
let longestAnimationFrameObserver: LongestAnimationFrameObserver | null = null;
let lastSentLoAFDuration = -1;
let loafCollector: LongAnimationFrameCollector | null = null;

// Отслеживание видимости окна для фильтрации событий, выполняющихся при скрытой вкладке.
// Событие считается валидным только если оно полностью произошло в интервале [lastPageVisibleTime, lastPageHiddenTime).
let lastPageVisibleTime = 0;
// Infinity = окно сейчас видимо, нет верхней границы для валидных событий.
// 0 = окно открылось в фоне (в скрытой вкладке), фильтруем все события до первого показа.
let lastPageHiddenTime = typeof document !== 'undefined' && document.visibilityState === 'hidden' ? 0 : Infinity;

/**
 * Флаг, который позволяет определить, была ли вкладка скрыта для текущей страницы
 * При переходах выставляется в false.
 */
let wasPageHidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';

// Детектор последовательных сетевых запросов
let requestWaterfallObserver: RequestWaterfallObserver | null = null;
let lastHWC = -1;

// Детектор событий скролла на странице
let scrollObserver: ScrollObserver | null = null;
let lastScrollJs = -1;
let lastScrollLatency = -1;

/** Остановить сбор target из событий интерактивности */
let stopCapturingInteractionTargets: null | (() => void) = null;

// Трекер Time To Interactive (учитывает CPU и сеть)
let ttiTracker: TimeToInteractiveTracker | null = null;

// Сохраняем оригинальный `sendDelta`, чтобы в `cleanupState` убрать обёртку
// и восстановить первоначальное поведение (важно прежде всего для тестов).
let originalRumSendDelta: typeof Ya.Rum.sendDelta | null = null;
let wrappedRumSendDeltaTarget: typeof Ya.Rum | null = null;

/** Событие готовности hero-элемента */
export let onHeroRenderEvent = createEvent();

/** Состояние готовности hero-элемента */
let isHeroRenderedFlag = false;

/**
 * Допуск на расхождение performance.now() между моментом фиксации записи браузером и моментом
 * её доставки в наш callback (микротаска PerformanceObserver, GC и т.п.).
 * Используется в проверках внутренней согласованности PerformanceEntry.
 */
const NOW_TOLERANCE_MS = 1_000;

/**
 * Верхняя граница для значений временных метрик, которые мы готовы передать в Rum счетчик.
 * Реальные значения INP / element-timing / loaf / scroll / tti не приближаются к этой границе.
 * Всё, что превышает этот порог, представлено исключительно мусором.
 */
const MAX_DELTA_MS = 120_000;

/**
 * Имена метрик в Rum счетчике, которые семантически являются временными интервалами в мс
 * и проверяются в обёртке `rum.sendDelta` ниже.
 * Намеренно перечислены явно: общая обёртка не должна затрагивать счетчики типа `hwc` или произвольные пользовательские метрики.
 */
const TIME_METRIC_NAMES: ReadonlySet<string> = new Set(['inp', 'loaf', 'tti', 'scroll_js', 'scroll_latency']);
const TIME_METRIC_PREFIXES: readonly string[] = ['element-timing-'];

function isTimeMetric(counterId: string): boolean {
    return TIME_METRIC_NAMES.has(counterId) || TIME_METRIC_PREFIXES.some(prefix => counterId.startsWith(prefix));
}

/**
 * Возвращает состояние готовности hero-элемента
 * @kind helper
 * @returns true, если hero-элемент отрендерился
 */
export function isHeroRendered() {
    return isHeroRenderedFlag;
}

/**
 * Хелпер, который позволяет определить доступен ли интерфейс Rum счетчика.
 *
 * @kind helper
 * @returns true, если интерфейс доступен, false — в обратном случае.
 */
function isRumAvailable() {
    return typeof window !== 'undefined' && Boolean(window.Ya?.Rum?.enabled);
}

/**
 * Получение инстанса Rum счетчика, если он был проинициализирован.
 *
 * @kind helper
 * @returns Инстанс Rum счетчика, если Rum недоступен или не проинициализирован — вернет undefined.
 */
function getRum() {
    if (!isRumAvailable() || !isRumInited) {
        return;
    }

    return window.Ya.Rum;
}

// Параметры для настройки Rum
interface RumOptions {
    requestWaterfallOptions?: RequestWaterfallObserverOptions;
}

/**
 * Инициализация бандловой части Rum счетчика.
 *
 * @kind helper
 * @param options — Параметры для настройки Rum
 * @returns undefined
 */
export function initRumBundle(options?: RumOptions) {
    if (isRumInited) {
        throw new RumError('RUM уже инициализирован', { cause: null });
    }

    if (!isRumAvailable()) {
        return;
    }

    require('@yandex-int/rum-counter/dist/bundle/implementation.min.js');
    require('@yandex-int/rum-counter/dist/bundle/onload.min.js');
    require('@yandex-int/rum-counter/dist/bundle/inp.min.js');

    isRumInited = true;

    const rum = getRum();
    if (rum) {
        wrapSendDelta(rum);

        onHeroRenderEvent.subscribe(() => {
            isHeroRenderedFlag = true;
        });

        stopCapturingInteractionTargets = startCapturingInteractionTargets();

        ttiTracker = new TimeToInteractiveTracker({
            onTrackEnd: (_name, timeToInteractive) => {
                sendDelta('tti', timeToInteractive);
            },
        });
        // Для MPA-страницы отсчитываем TTI от начала навигации
        ttiTracker.track('tti', 0);

        longestAnimationFrameObserver = new LongestAnimationFrameObserver(isEntryValid);
        longestAnimationFrameObserver?.start(true);

        const elementTimingObserver = new ElementTimingObserver(isEntryValid);
        setElementTimingObserver(elementTimingObserver);
        elementTimingObserver.subscribeOnceByElementIdentifier(
            ({
                identifier,
                renderTimeFromLastNavigation,
                renderTime,
                element,
                pageChangedTimestamp: pageStartTime,
            }) => {
                const isHero = identifier === HERO_ELEMENT_ID;

                performance.mark('element-timing', {
                    startTime: renderTime,
                    detail: {
                        identifier,
                        renderTimeFromLastNavigation,
                        devtools: {
                            dataType: 'marker',
                            // eslint-disable-next-line @adv-frontend/config/no-rus-string-locales-rule -- строки только для разработчиков
                            tooltipText: `Отрисован ${isHero ? 'hero ' : ''}элемент: ${identifier}`,
                            color: isHero ? 'primary' : 'secondary',
                        },
                    },
                });

                if (isHero) {
                    onHeroRenderEvent.publish();
                }

                // Если вкладка скрывалась на этой странице, то не отправляем метрики hero-элементов, так как они вероятно не корректные из замедления страницы в фоне
                if (wasPageHidden) {
                    return;
                }
                debugLog(() => ({
                    tags: ['ElementTiming', identifier, renderTimeFromLastNavigation.toFixed(2)],
                    details: {
                        renderTime: renderTime,
                        pageChangedTimestamp: pageStartTime,
                    },
                    htmlRef: element,
                }));

                rum.sendDelta(`element-timing-${identifier}`, renderTimeFromLastNavigation);
            }
        );
        elementTimingObserver.start();

        requestWaterfallObserver = createRequestWaterfallObserver(options?.requestWaterfallOptions);
        requestWaterfallObserver.observe();

        scrollObserver = createScrollObserver(isEntryValid);
        scrollObserver.observe();

        unsubscribePageHide = subscribeToPageHide(onPageHide);
        unsubscribePageShow = subscribeToPageShow(onPageShow);

        provideAttribution();
    }
}

/**
 * Сбрасывает состояние Rum счетчика.
 *
 * @unusedCodePublic Используется в тестах.
 * @kind helper
 * @returns undefined
 */
export function cleanupState() {
    cleanupElementTimingObserver();

    unwrapSendDelta();

    ttiTracker?.destroy();
    ttiTracker = null;

    longestAnimationFrameObserver?.dispose();
    longestAnimationFrameObserver = null;
    lastSentLoAFDuration = -1;

    lastPageVisibleTime = 0;
    lastPageHiddenTime = typeof document !== 'undefined' && document.visibilityState === 'hidden' ? 0 : Infinity;

    requestWaterfallObserver?.disconnect();
    requestWaterfallObserver = null;
    lastHWC = -1;

    stopCapturingInteractionTargets?.();

    scrollObserver?.disconnect();
    scrollObserver = null;
    lastScrollJs = -1;
    lastScrollLatency = -1;
    onHeroRenderEvent = createEvent();
    isHeroRenderedFlag = false;

    unsubscribePageHide?.();
    unsubscribePageShow?.();

    globalSubPageIndex = 0;

    isRumInited = false;
}

/**
 * Отправить счетчик с дельтой времени.
 *
 * @kind helper
 * @param name - ID метки времени.
 * @param delta - Точное значение дельты.
 * @param additional - Дополнительные данные. Должны быть простым key-value объектом.
 * @returns undefined
 */
export function sendDelta(name: string, delta: number, additional?: Record<string, Primitive>) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    let params: Ya.Rum.RumParams | undefined = undefined;
    if (additional) {
        params = {
            '-additional': encodeURIComponent(makeAdditional(additional)),
        };
    }

    debugLog(() => ({
        tags: ['Delta', name, delta.toFixed(2)],
        details: {
            additional,
        },
    }));
    rum.sendDelta(name, delta, params);
}

/**
 * Отправить счетчик с временной меткой.
 *
 * @kind helper
 * @param name - ID метки времени.
 * @param time - Время. Если не передано, вычисляется время от начала навигации до момента вызова функции.
 * @returns undefined
 */
export function sendTimeMark(name: string, time?: number) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({
        tags: ['Time mark', name, time?.toFixed(2)],
    }));
    rum.sendTimeMark(name, time);
}

/**
 * Получение '-additional' параметра Rum счетчика.
 *
 * @kind helper
 * @returns Значение параметра additional в распаршенном виде.
 */
export function getAdditional() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    const rawAdditional = rum._vars['-additional'];
    if (!rawAdditional) {
        return;
    }

    try {
        const parsedAdditional = JSON.parse(rawAdditional);

        if (isObject(parsedAdditional)) {
            return parsedAdditional;
        }

        throw new RumError('Содержимое поля additional в RUM не является объектом', { cause: null });
    } catch (error) {
        throw new RumError('Ошибка парсинга additional в RUM', { cause: error });
    }
}

/**
 * Отложенная установка имени просматриваемой страницы.
 *
 * @kind helper
 * @param pageName — Имя просматриваемой страницы.
 * @returns undefined
 */
export function setDeferredPageName(pageName: string) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({ tags: ['Set page name', pageName] }));

    rum.setDeferredPageName(pageName);
}

/**
 * Мета-информация о странице.
 */
export interface SetRumPageProps {
    /**
     * Человеко-понятное название страницы на латинице.
     */
    pageName: string;
}
/**
 * Установка имени просматриваемой страницы.
 *
 * @kind helper
 * @param {SetRumPageProps} props Параметры для установки имени просматриваемой страницы.
 * @returns undefined
 */
export function setRumPage(props: SetRumPageProps) {
    setDeferredPageName(props.pageName);
}

/**
 * Регистрация SubPage'а при SPA переходе.
 *
 * @kind helper
 * @returns undefined
 */
export function registerSubPage() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    debugLog(() => ({ tags: ['Register SubPage'] }));

    // Сохраняем время начала регистрации подстраницы.
    // Это время пригодится нам для отправки TTI и для сохранения информации в Rum счетчике.
    const registerSubPageStartTime = rum.getTime();

    // Отправляем все накопленные данные в Rum.
    sendHighestWaterfallCount();
    sendScrollJsMetric();
    sendScrollLatencyMetric();

    // Посылаем самый длинный LoAF для текущей страницы
    sendLongestAnimationFrame();
    resetLongestAnimationFrame();

    // Сбрасываем все метрики для новых замеров на новой странице.
    resetHighestWaterfallCount();
    resetScrollMetrics();

    // Завершаем сессию.
    rum.completeSession(true);

    // Сбрасываем имя текущей страницы, чтобы новые метрики отправлялись уже для новой.
    rum.resetDeferredPageName();

    // Триггерим отправку TTI в Rum для новой страницы, отсчет берем от начала регистрации подстраницы.
    rum.sendTTI(undefined, registerSubPageStartTime);

    // Начинаем новый замер TTI для новой страницы.
    // track() сам сбрасывает предыдущий замер и переносит незавершённые сетевые запросы.
    ttiTracker?.track('tti', registerSubPageStartTime);

    // Очищаем собранные LoAF.
    // INP сбрасывается после completeSession, значит хранить их нет смысла
    loafCollector?.clear();

    // Перезапускаем ElementTimingObserver, указываем точку отсчета новой страницы.
    getElementTimingObserver()?.restart({ pageChangedTimestamp: performance.now() });
    isHeroRenderedFlag = false;
    wasPageHidden = false;

    // Собираем page_id из параметров счетчика
    const project = rum._vars['-project'] ?? 'unknown';
    const service = rum._vars['-service'];
    const platform = rum._vars['-platform'] ?? 'unknown';
    const subPageId = buildRumId({ project, service, platform });

    // Формируем subPageIndex и actionStartTime для нового subpage'а
    const subPageIndex = String(globalSubPageIndex++);
    const actionStartTime = String(registerSubPageStartTime);

    // Получаем текущий additional и удаляем is_bfcache, так как SPA-переход не является BFCache restore
    const currentAdditional = getAdditional() ?? {};
    delete currentAdditional.is_bfcache;

    // Добавляем данные о subpage'е в additional для реалтаймовых метрик
    const additional = safeJsonStringify({
        ...currentAdditional,
        subpage_name: subPageId,
        subpage_index: subPageIndex,
        action_start_time: actionStartTime,
    });

    // При обновлении параметров важно сохранить ID из BlockStat'а,
    // т.к. на них завязан код Тренчера.
    rum.setVars({
        rum_id: subPageId,
        '-page': subPageId,
        '-additional': additional,
        [BlockStat.SubPageName]: subPageId,
        [BlockStat.SubPageIndex]: subPageIndex,
        [BlockStat.ActionStartTime]: actionStartTime,
    });
}

/**
 * Регистрация текущего URL страницы в параметрах RUM счетчика.
 *
 * @kind helper
 */
export function registerUrl() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    const url = buildRumUrl();

    debugLog(() => ({ tags: ['Register URL'], details: { url } }));

    // Докидываем URL в '-additional', не используя поле '-url',
    // потому что поле '-url' не прорастает в ClickHouse таблицы.
    rum.setVars({ '-additional': makeAdditional({ url }) });
}

/**
 * Отправляет метрику с информацией о самом длинном кадре
 */
function sendLongestAnimationFrame() {
    const rum = getRum();
    if (!rum || !longestAnimationFrameObserver) {
        return;
    }

    const longestAnimationFrame = longestAnimationFrameObserver.getLongestAnimationFrame();
    if (!longestAnimationFrame || lastSentLoAFDuration >= longestAnimationFrame.duration) {
        return;
    }

    sendDelta('loaf', longestAnimationFrame.duration, getLoAFAdditionalParams(longestAnimationFrame));
    lastSentLoAFDuration = longestAnimationFrame.duration;
}

/**
 * Логирует максимальный waterfall запросов.
 */
function sendHighestWaterfallCount() {
    const rum = getRum();
    if (!rum || !requestWaterfallObserver?.supported()) {
        return;
    }

    const waterfall = requestWaterfallObserver.getHighestWaterfall();
    const hwc = Math.max(waterfall.requestsCount - 1, 0);

    // Не логируем повторно одно и то же значение
    if (lastHWC === hwc) {
        return;
    }

    const attribution = hwc > 0 ? waterfall.attribution : [];
    const names = attribution.map(({ name }) => encodeURIComponent(name)).join(',');
    const durations = attribution.map(({ duration }) => duration).join(',');

    debugLog(() => ({
        tags: ['HWC', hwc.toString()],
        details: attribution,
    }));
    sendDelta('hwc', hwc, {
        hwc_waterfall: names,
        hwc_duration: durations,
        hwc_total_duration: waterfall.totalDuration,
    });

    lastHWC = hwc;
}

/**
 * Cбрасывает метрику максимального waterfall запросов.
 */
function resetHighestWaterfallCount() {
    lastHWC = -1;
    requestWaterfallObserver?.reset();
}

function resetLongestAnimationFrame() {
    lastSentLoAFDuration = -1;
    longestAnimationFrameObserver?.resetLongestAnimationFrame();
}

/**
 * Логирует максимальную задержку обработки JS при скролле.
 */
function sendScrollJsMetric() {
    const rum = getRum();
    if (!rum || !scrollObserver) {
        return;
    }

    const scrollJsMetric = scrollObserver.getScrollJsMetric();
    if (!scrollJsMetric) {
        return;
    }

    const { time, attribution } = scrollJsMetric;

    // Не логируем повторно одно и то же значение
    if (time === lastScrollJs) {
        return;
    }

    debugLog(() => ({ tags: ['Scroll JS', String(time)], details: attribution }));
    // Добавляем в additional поле scroll_js_hrtime с метрикой, чтобы строить более точные графики.
    // Потому что в таблице rum.delta метрика хранится, как целое число.
    sendDelta('scroll_js', time, {
        scroll_js_hrtime: time,
        scroll_js_selector: attribution.selector,
    });

    lastScrollJs = time;
}

/**
 * Логирует максимальную задержку отрисовки при скролле.
 */
function sendScrollLatencyMetric() {
    const rum = getRum();
    if (!rum || !scrollObserver) {
        return;
    }

    const scrollLatencyMetric = scrollObserver.getScrollLatencyMetric();
    if (!scrollLatencyMetric) {
        return;
    }

    const { time, attribution } = scrollLatencyMetric;

    // Не логируем повторно одно и то же значение
    if (time === lastScrollLatency) {
        return;
    }

    debugLog(() => ({ tags: ['Scroll Latency', String(time)], details: attribution }));
    // Добавляем в additional поле scroll_latency_hrtime с метрикой, чтобы строить более точные графики.
    // Потому что в таблице rum.delta метрика хранится, как целое число.
    sendDelta('scroll_latency', time, {
        scroll_latency_hrtime: time,
        scroll_latency_selector: attribution.selector,
    });

    lastScrollLatency = time;
}

/**
 * Сбрасывает метрики скролла.
 */
function resetScrollMetrics() {
    lastScrollJs = -1;
    lastScrollLatency = -1;
    scrollObserver?.reset();
}

/**
 * Установка параметров в Rum счетчике для сборка доп. информации об LCP, CLS, INP.
 *
 * @kind helper
 * @returns undefined
 */
function provideAttribution() {
    const rum = getRum();
    if (!rum) {
        return;
    }

    // Достаточно, чтобы найти LoAF, которые могут быть связаны с INP
    const bufferSize = 50;
    loafCollector = new LongAnimationFrameCollector(bufferSize);
    loafCollector.start(true);

    rum.getCLSAdditionalParams = createAttributionProvider(getCLSAdditionalParams);
    rum.getINPAdditionalParams = createAttributionProvider(performanceEventTiming => {
        return getINPAdditionalParams(
            performanceEventTiming,
            loafCollector?.getRelatedLoAFs(performanceEventTiming.startTime, performanceEventTiming.processingEnd)
        );
    });
    rum.getLCPAdditionalParams = createAttributionProvider(getLCPAdditionalParams);
}

/**
 * Коллбэк, который вызовется на скрытие/закрытие страницы.
 */
function onPageHide() {
    wasPageHidden = true;
    lastPageHiddenTime = performance.now();

    debugLog(() => ({ tags: ['Page hide'] }));
    // Останавливаем сбор метрики, так как метрики в таком случае не валидные:
    // вкладка в фоне работает медленнее (inactive tab throttling), а beforeunload блокирует основной поток.
    getElementTimingObserver()?.stop();

    // Отменяем замер TTI: в скрытой вкладке метрика не валидна из-за throttling.
    ttiTracker?.cancel();

    sendLongestAnimationFrame();
    longestAnimationFrameObserver?.stop();
    loafCollector?.stop();

    sendHighestWaterfallCount();
    sendScrollJsMetric();
    sendScrollLatencyMetric();
    scrollObserver?.reset();
}

/**
 * Коллбэк, который вызовется на показ страницы после скрытия.
 */
function onPageShow(event: Event) {
    lastPageVisibleTime = performance.now();
    lastPageHiddenTime = Infinity;

    const isBFCache = event instanceof PageTransitionEvent && event.persisted;
    if (isBFCache) {
        updateAdditional({ is_bfcache: '1' });
    }

    debugLog(() => ({ tags: ['Page show', isBFCache ? 'bfcache' : undefined] }));
    longestAnimationFrameObserver?.start();
    loafCollector?.start();
    scrollObserver?.reset();
    getElementTimingObserver()?.continue();
}

/**
 * Создает новый сериализованный объект additional на основе текущего значения в Rum счетчике.
 *
 * @param obj Параметры, которые нужно добавить в addditional.
 * @returns Сериализованый объект obj, смерженный с текущим значением additional
 */
function makeAdditional<T extends JSONCompatible<T>>(obj: T) {
    const additional = getAdditional() ?? {};
    Object.assign(additional, obj);
    return safeJsonStringify(additional);
}

/**
 * Создает провайдер дополнительных параметров для метрик,
 * правильно добавляя все значения в additional.
 *
 * @param getAttribution Метод для получения доп. данных по логируемой метрике.
 * @returns Метод для сбора доп. данных по метрике, с правильным формированием параметров для RUM.
 */
function createAttributionProvider<T>(getAttribution: (event: T) => Record<string, string> | undefined) {
    return (event: T) => {
        const attribution = getAttribution(event);

        return {
            '-additional': encodeURIComponent(makeAdditional(attribution)),
        };
    };
}

/**
 * Проверяет, что событие произошло полностью при видимом окне, и что у него внутренне непротиворечивые
 * временные поля. Покрывает четыре случая:
 * - событие началось до последнего возврата из фона
 * - событие закончилось после скрытия окна
 * - окно сейчас скрыто
 * - запись имеет некорректные тайминги (отрицательные значения, либо `startTime + duration` уходит
 *   в будущее относительно `performance.now()` - такие записи на Android Chromium для keydown содержат значения порядка дней / `INT32_MAX` мс).
 * @kind helper
 * @param entry PerformanceEntry
 * @returns признак валидности события
 */
function isEntryValid(entry: PerformanceEntry): boolean {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return false;
    }
    const startTime = entry.startTime;
    const endTime = entry.startTime + entry.duration;

    // Внутренняя согласованность: реальные PerformanceEntry никогда не имеют отрицательных таймингов
    // и не заканчиваются в будущем относительно момента, когда мы их получили.
    if (startTime < 0 || entry.duration < 0) {
        return false;
    }

    const now = performance.now();

    if (startTime > now || endTime > now + NOW_TOLERANCE_MS) {
        return false;
    }

    if (startTime < lastPageVisibleTime || endTime > lastPageHiddenTime) {
        return false;
    }

    return true;
}

/**
 * Оборачивает `rum.sendDelta`, чтобы не отправлять некорректные значения временных метрик.
 *
 * INP попадает в Rum счетчик напрямую из вендорного `inp.js`, минуя экспорт `sendDelta` из этого
 * модуля, поэтому фильтрацию приходится ставить на уровне `Ya.Rum.sendDelta` - это единственная
 * общая точка отправки. Для остальных временных метрик (`loaf`, `tti`, `scroll_*`, `element-timing-*`)
 * обёртка работает как защита от регрессий - основная фильтрация делается ещё на этапе
 * `isEntryValid` в обсервере.
 *
 * Проверки:
 * 1. NaN / Infinity / отрицательные значения - обрезаем как заведомо некорректные
 * 2. `delta > performance.now() + NOW_TOLERANCE_MS` - интервал не может заканчиваться в будущем
 * относительно момента отправки. Эта проверка покрывает большинство аномальных INP, в том числе
 * значения вида `INT32_MAX`.
 * 3. `delta > MAX_DELTA_MS` - крайние случаи аномальных значение, когда внутренне
 * согласованная запись всё равно содержит явно нереальное значение
 */
function wrapSendDelta(rum: typeof Ya.Rum) {
    if (wrappedRumSendDeltaTarget === rum) {
        return;
    }

    const original = rum.sendDelta.bind(rum);

    originalRumSendDelta = rum.sendDelta;
    wrappedRumSendDeltaTarget = rum;

    rum.sendDelta = function sendDeltaWithSanityChecks<CounterId extends string>(
        counterId: CounterId,
        delta?: number,
        ...rest: [params?: Ya.Rum.RumParams, opts?: Record<string, unknown>]
    ): void {
        if (typeof delta === 'number' && isTimeMetric(counterId)) {
            if (!Number.isFinite(delta) || delta < 0) {
                debugLog(() => ({ tags: ['rum/drop', counterId, 'invalid', String(delta)] }));
                return;
            }

            if (delta > performance.now() + NOW_TOLERANCE_MS) {
                debugLog(() => ({ tags: ['rum/drop', counterId, 'future', String(delta)] }));
                return;
            }

            if (delta > MAX_DELTA_MS) {
                debugLog(() => ({ tags: ['rum/drop', counterId, 'anomaly', String(delta)] }));
                return;
            }
        }

        return original(counterId, delta, ...rest);
    };
}

function unwrapSendDelta() {
    if (wrappedRumSendDeltaTarget && originalRumSendDelta) {
        wrappedRumSendDeltaTarget.sendDelta = originalRumSendDelta;
    }
    wrappedRumSendDeltaTarget = null;
    originalRumSendDelta = null;
}

/**
 * Обновляет дополнительные параметры в Rum счетчике.
 * @param vars - дополнительные параметры, которые нужно добавить в additional.
 */
export function updateAdditional(vars: Record<string, string>) {
    const rum = getRum();
    if (!rum) {
        return;
    }

    rum.setVars({ '-additional': makeAdditional(vars) });
}
